import {
  ConditionBuilder,
  type Condition,
  type ScalarValue,
  type SimpleCondition,
} from "@tsmono/inspect-common/query";
import { parseDateFromInput } from "@tsmono/util";

import type { FilterCondition, FilterSpec, FilterType } from "./types";

/**
 * Escape `%`, `_` and `\` so user text matches literally inside a LIKE
 * pattern. Consumers evaluating LIKE must treat `\` as an escape (the client
 * evaluator's `likeToRegExp`); server-side SQL must use `ESCAPE '\'`.
 */
export const escapeLikePattern = (text: string): string =>
  text.replace(/[\\%_]/g, "\\$&");

const parseScalar = (
  filterType: FilterType,
  raw: string
): ScalarValue | undefined => {
  switch (filterType) {
    case "number":
    case "duration": {
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    case "boolean":
      if (raw === "true") return true;
      if (raw === "false") return false;
      return undefined;
    case "date":
    case "datetime":
      return parseDateFromInput(raw);
    case "string":
    case "unknown":
    default:
      return raw;
  }
};

const parseList = (
  filterType: FilterType,
  raw: string
): ScalarValue[] | undefined => {
  const parts = raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s !== "");
  const parsed: ScalarValue[] = [];
  for (const part of parts) {
    const value = parseScalar(filterType, part);
    if (value === undefined) return undefined;
    parsed.push(value);
  }
  return parsed;
};

/**
 * Compile a single `FilterCondition` into a wire `SimpleCondition`.
 *
 * Returns `null` when the condition is a no-op (empty/incomplete value —
 * callers treat this as "clear the filter") and `undefined` when the raw
 * input can't be parsed for the column's type (invalid — don't apply).
 */
function conditionToWire(
  columnId: string,
  filterType: FilterType,
  cond: FilterCondition
): SimpleCondition | null | undefined {
  const { operator, value, value2 } = cond;

  if (operator === "is blank") {
    return ConditionBuilder.simple(columnId, "IS NULL", null);
  }
  if (operator === "is not blank") {
    return ConditionBuilder.simple(columnId, "IS NOT NULL", null);
  }

  if (value.trim() === "") return null;

  // Explicit cases (not Set.has checks) so TypeScript narrows `operator` to
  // the comparison literals shared with the wire enum in the final group.
  switch (operator) {
    case "contains":
      return ConditionBuilder.simple(
        columnId,
        "ILIKE",
        `%${escapeLikePattern(value)}%`
      );
    case "does not contain":
      return ConditionBuilder.simple(
        columnId,
        "NOT ILIKE",
        `%${escapeLikePattern(value)}%`
      );
    case "starts with":
      return ConditionBuilder.simple(
        columnId,
        "ILIKE",
        `${escapeLikePattern(value)}%`
      );
    case "ends with":
      return ConditionBuilder.simple(
        columnId,
        "ILIKE",
        `%${escapeLikePattern(value)}`
      );
    case "in":
    case "not in": {
      const parsed = parseList(filterType, value);
      if (parsed === undefined) return undefined;
      if (parsed.length === 0) return null;
      return ConditionBuilder.simple(
        columnId,
        operator === "in" ? "IN" : "NOT IN",
        parsed
      );
    }
    case "between":
    case "not between": {
      if (!value2 || value2.trim() === "") return null;
      const start = parseScalar(filterType, value);
      const end = parseScalar(filterType, value2);
      if (start === undefined || end === undefined) return undefined;
      return ConditionBuilder.simple(
        columnId,
        operator === "between" ? "BETWEEN" : "NOT BETWEEN",
        [start, end]
      );
    }
    case "=":
    case "!=":
    case "<":
    case "<=":
    case ">":
    case ">=": {
      const parsed = parseScalar(filterType, value);
      if (parsed === undefined) return undefined;
      return ConditionBuilder.simple(columnId, operator, parsed);
    }
    default: {
      operator satisfies never;
      return undefined;
    }
  }
}

/**
 * Compile a `FilterSpec` into a wire `Condition` — a plain `SimpleCondition`
 * for a single condition, or a compound AND/OR when a `second` condition is
 * present.
 *
 * Returns `null` when the spec is a no-op (empty/incomplete value — callers
 * treat this as "clear the filter") and `undefined` when the raw input can't
 * be parsed for the column's type (invalid — don't apply). When a `second`
 * condition is present: if it's a no-op, the primary alone is returned; if
 * it's invalid, the whole spec is invalid.
 */
export function specToCondition(
  columnId: string,
  filterType: FilterType,
  spec: FilterSpec
): Condition | null | undefined {
  const primary = conditionToWire(columnId, filterType, spec);
  if (primary === null || primary === undefined) return primary;

  if (spec.second && spec.join) {
    const secondary = conditionToWire(columnId, filterType, spec.second);
    if (secondary === undefined) return undefined;
    if (secondary === null) return primary;
    return spec.join === "and" ? primary.and(secondary) : primary.or(secondary);
  }

  return primary;
}
